import { useCallback, useEffect, useRef, useState } from "react";
import { SettingsRequestError, settingsApi } from "./api";
import type { CheckedVoiceSource, SynthesisSettings, VoiceStudioJob } from "./types";

const ACTIVE_JOB_POLL_MS = 2_500;

function durationLabel(durationMs: number | null): string {
  if (durationMs === null) return "时长待生成";
  const seconds = Math.max(0, Math.round(durationMs / 1_000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function statusLabel(job: VoiceStudioJob): string {
  if (job.status === "queued") return "排队中";
  if (job.status === "generating") return "生成中";
  if (job.status === "failed") return `生成失败${job.error_code ? ` · ${job.error_code}` : ""}`;
  return durationLabel(job.duration_ms);
}

export function VoiceStudio({
  voiceId,
  settings,
  canGenerate,
  saveLabel,
  saving,
  canSave,
  onSave,
  onApply,
}: {
  voiceId: string | null;
  settings: SynthesisSettings;
  canGenerate: boolean;
  saveLabel: string;
  saving: boolean;
  canSave: boolean;
  onSave: () => Promise<void>;
  onApply: (source: CheckedVoiceSource, settings: SynthesisSettings) => void;
}) {
  const [text, setText] = useState("");
  const [jobs, setJobs] = useState<VoiceStudioJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [actionError, setActionError] = useState("");
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const audioRefs = useRef(new Map<string, HTMLAudioElement>());
  const audioUrls = useRef(new Map<string, string>());
  const pendingRequest = useRef<{ snapshot: string; id: string } | null>(null);

  const refreshJobs = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoadingJobs(true);
    try {
      setJobs(await settingsApi.loadVoiceStudioJobs());
      setActionError("");
    } catch {
      if (!quiet) setActionError("无法读取已保存音频，请稍后重试。");
    } finally {
      if (!quiet) setLoadingJobs(false);
    }
  }, []);

  useEffect(() => { void refreshJobs(); }, [refreshJobs]);
  useEffect(() => {
    if (!jobs.some((job) => job.status === "queued" || job.status === "generating")) return;
    const timer = window.setInterval(() => { void refreshJobs(true); }, ACTIVE_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [jobs, refreshJobs]);
  useEffect(() => () => {
    for (const audio of audioRefs.current.values()) audio.pause();
    for (const url of audioUrls.current.values()) URL.revokeObjectURL(url);
    audioRefs.current.clear(); audioUrls.current.clear();
  }, []);

  async function generate(): Promise<void> {
    const trimmed = text.trim();
    if (!voiceId || !canGenerate || !trimmed || generating) return;
    setGenerating(true); setActionError("");
    try {
      const snapshot = JSON.stringify({ text: trimmed, voice_id: voiceId, synthesis_settings: settings });
      if (pendingRequest.current?.snapshot !== snapshot) pendingRequest.current = { snapshot, id: crypto.randomUUID() };
      const job = await settingsApi.createVoiceStudioJob({ request_id: pendingRequest.current.id, text: trimmed, voice_id: voiceId, synthesis_settings: settings });
      pendingRequest.current = null;
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)]);
    } catch { setActionError("音频任务尚未确认创建成功，请刷新列表核对；相同内容重试将复用原请求。"); }
    finally { setGenerating(false); }
  }

  async function togglePlayback(job: VoiceStudioJob): Promise<void> {
    if (job.status !== "completed") return;
    const current = audioRefs.current.get(job.id);
    if (playingId === job.id && current) { current.pause(); setPlayingId(null); return; }
    for (const [id, audio] of audioRefs.current) { if (id !== job.id) audio.pause(); }
    try {
      let audio = current;
      if (!audio) {
        const blob = await settingsApi.loadVoiceStudioAudio(job.id);
        const url = URL.createObjectURL(blob);
        audioUrls.current.set(job.id, url);
        audio = document.createElement("audio");
        audio.src = url;
        audio.preload = "auto";
        audio.addEventListener("ended", () => setPlayingId((id) => id === job.id ? null : id));
        audioRefs.current.set(job.id, audio);
      }
      await audio.play(); setPlayingId(job.id); setActionError("");
    } catch { setActionError("无法播放这段音频，请稍后重试。"); }
  }

  async function applyJob(job: VoiceStudioJob): Promise<void> {
    if (applyingId !== null) return;
    setApplyingId(job.id); setActionError("");
    try {
      const source = await settingsApi.checkVoiceSource(job.voice_id);
      onApply(source, job.synthesis_settings);
    } catch { setActionError("无法重新确认这段音频使用的声源，未套用设置。"); }
    finally { setApplyingId(null); }
  }

  async function saveTitle(job: VoiceStudioJob): Promise<void> {
    const title = editingTitle.trim();
    setEditingId(null);
    if (!title || title === job.title) return;
    try {
      const updated = await settingsApi.updateVoiceStudioJobTitle(job.id, title);
      setJobs((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch { setActionError("标题修改失败，请稍后重试。"); }
  }

  async function deleteJob(job: VoiceStudioJob): Promise<void> {
    if (job.status === "queued" || job.status === "generating") { setActionError("音频仍在生成中，暂时不能删除。"); return; }
    if (!window.confirm(`确定永久删除“${job.title}”吗？此操作无法撤销。`)) return;
    try {
      await settingsApi.deleteVoiceStudioJob(job.id);
      audioRefs.current.get(job.id)?.pause();
      const url = audioUrls.current.get(job.id); if (url) URL.revokeObjectURL(url);
      audioRefs.current.delete(job.id); audioUrls.current.delete(job.id);
      setJobs((current) => current.filter((item) => item.id !== job.id));
      if (playingId === job.id) setPlayingId(null);
    } catch (error) { setActionError(error instanceof SettingsRequestError && error.code === "voice_studio_job_in_progress" ? "音频仍在生成中，暂时不能删除。" : "音频删除失败，请稍后重试。"); }
  }

  return <>
    <section className="voice-studio-script" aria-label="试听文本">
      <h4>试听文本</h4>
      <textarea aria-label="带语气标记的试听文本" maxLength={20_000} value={text} onChange={(event) => setText(event.target.value)} placeholder="输入台词；可以直接使用 Fish Audio 支持的语气标记。" />
      {!voiceId || !canGenerate ? <p className="voice-studio-hint">请先在左栏输入并成功检查声源。</p> : null}
      <footer>
        <button className="primary" type="button" disabled={!canGenerate || !text.trim() || generating} onClick={() => void generate()}>{generating ? "提交中…" : "生成"}</button>
        <button className="primary" type="button" disabled={!canSave} onClick={() => void onSave()}>{saving ? "保存中…" : saveLabel}</button>
      </footer>
    </section>
    <section className="voice-studio-library" aria-label="永久音频列表">
      <div className="voice-studio-library-title"><h4>已保存音频</h4><button type="button" className="secondary outlined" disabled={loadingJobs} onClick={() => void refreshJobs()} aria-label="刷新音频列表">↻</button></div>
      {loadingJobs ? <p className="voice-studio-empty">正在读取…</p> : jobs.length === 0 ? <p className="voice-studio-empty">还没有生成过音频。</p> : <div className="voice-studio-jobs">{jobs.map((job) => <article className={`voice-studio-job is-${job.status}`} key={job.id}>
        <button type="button" className="voice-studio-fab play" aria-label={`${playingId === job.id ? "暂停" : "播放"}${job.title}`} disabled={job.status !== "completed"} onClick={() => void togglePlayback(job)}>{playingId === job.id ? "Ⅱ" : "▶"}</button>
        <div className="voice-studio-job-copy">
          {editingId === job.id ? <input aria-label="音频标题" autoFocus maxLength={200} value={editingTitle} onChange={(event) => setEditingTitle(event.target.value)} onBlur={() => void saveTitle(job)} onKeyDown={(event) => { if (event.key === "Enter") void saveTitle(job); if (event.key === "Escape") setEditingId(null); }} /> : <strong title="双击修改标题" onDoubleClick={() => { setEditingId(job.id); setEditingTitle(job.title); }}>{job.title}</strong>}
          <span>{statusLabel(job)}</span>
        </div>
        <div className="voice-studio-job-actions">
          <button type="button" className="voice-studio-fab" aria-label={`套用${job.title}的设置`} disabled={applyingId !== null} onClick={() => void applyJob(job)}>{applyingId === job.id ? "…" : "↙"}</button>
          <button type="button" className="voice-studio-fab danger" aria-label={`删除音频${job.title}`} disabled={job.status === "queued" || job.status === "generating"} onClick={() => void deleteJob(job)}>×</button>
        </div>
      </article>)}</div>}
      {actionError ? <p className="action-error" role="alert">{actionError}</p> : null}
    </section>
  </>;
}
